import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, RotateCcw, RefreshCw, Shuffle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Label } from "@/pages/admin/AdminLayout";

export function WatermarkUpload({ custom, onChange, mp }) {
  const [ver, setVer] = useState(0);
  return (
    <div className="mt-6 pt-5 border-t border-[#282520] flex flex-wrap items-center gap-5" data-testid="watermark-upload">
      <WatermarkImage custom={custom} onChange={(v) => { onChange(v); setVer((x) => x + 1); }} />
      <WatermarkPreview mp={mp} ver={ver} />
      <RewatermarkAll />
    </div>
  );
}

export function WatermarkImage({ segment = "", custom, onChange }) {
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const q = segment ? `?segment=${encodeURIComponent(segment)}` : "";
  const tid = segment ? `-seg-${segment.toLowerCase().replace(/\s+/g, "-")}` : "";
  const load = () => api.get(`/admin/settings/watermark${q}`, { responseType: "blob" }).then((r) => setSrc((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [segment]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/settings/watermark${q}`, fd); toast.success("Marca de água atualizada. Aplica-se às próximas fotos enviadas."); onChange(true); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const reset = async () => {
    if (!window.confirm(segment ? "Voltar a usar a imagem geral neste segmento?" : "Voltar ao emblema GoldenTime original?")) return;
    try { await api.delete(`/admin/settings/watermark${q}`); toast.success("Imagem reposta"); onChange(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex flex-wrap items-center gap-5 flex-1 min-w-[280px]">
      <div className="w-28 h-28 border border-[#282520] flex items-center justify-center p-3" style={{ background: "repeating-conic-gradient(#2a2722 0% 25%, #1a1814 0% 50%) 50% / 16px 16px" }}>
        {src && <img src={src} alt="Marca de água" className="max-w-full max-h-full object-contain" data-testid={`watermark-preview${tid}`} />}
      </div>
      <div className="flex-1 min-w-[220px]">
        <Label>Imagem da marca de água {segment ? (custom ? "(própria do segmento)" : "(usa a imagem geral)") : custom ? "(personalizada)" : "(emblema GoldenTime)"}</Label>
        <p className="text-xs text-[#A39E93] mb-3">PNG com fundo transparente recomendado. Se a imagem não tiver transparência, o fundo escuro é removido automaticamente. Aplica-se às novas fotos; use "Reaplicar" para atualizar as existentes.</p>
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept="image/png,image/webp,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`watermark-file-input${tid}`} />
          <button type="button" className="btn-line !py-2 text-xs" disabled={busy} onClick={() => input.current?.click()} data-testid={`watermark-upload-button${tid}`}><Upload className="w-3.5 h-3.5" /> {busy ? "A carregar…" : "Carregar imagem"}</button>
          {custom && <button type="button" className="btn-line !py-2 text-xs" onClick={reset} data-testid={`watermark-reset-button${tid}`}><RotateCcw className="w-3.5 h-3.5" /> {segment ? "Usar imagem geral" : "Repor emblema original"}</button>}
        </div>
      </div>
    </div>
  );
}

export function WatermarkPreview({ mp, ver, segment = "", tid = "" }) {
  const [idx, setIdx] = useState(0);
  const [img, setImg] = useState("");
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(mp || {}) + segment;
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.post("/admin/media/watermark-preview", { media_processing: mp || {}, index: idx, segment }, { responseType: "blob" })
        .then((r) => { setErr(""); setCount(Number(r.headers["x-preview-count"] || 0)); setImg((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); }); })
        .catch(async (e) => {
          if (e.response?.status === 404) return setErr("Ainda não há fotos publicadas com original para pré-visualizar.");
          try { setErr(JSON.parse(await e.response.data.text()).detail || "Erro na pré-visualização."); } catch { setErr("Erro na pré-visualização."); }
        })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, idx, ver]);
  return (
    <div className="w-full mt-2 pt-4 border-t border-[#282520]" data-testid={`watermark-live-preview${tid}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Label>Pré-visualização numa foto real do catálogo</Label>
        {count > 1 && <button type="button" className="btn-line !py-1 !px-2.5 text-xs" onClick={() => setIdx((i) => i + 1)} data-testid={`watermark-preview-next${tid}`}><Shuffle className="w-3.5 h-3.5" /> Outra foto</button>}
        {loading && <span className="text-xs text-[#A39E93]">A atualizar…</span>}
      </div>
      {err ? <p className="text-xs text-[#A39E93]" data-testid={`watermark-preview-empty${tid}`}>{err}</p>
        : img && <img src={img} alt="Pré-visualização da marca de água" className="max-h-[420px] w-auto border border-[#282520]" data-testid={`watermark-preview-photo${tid}`} />}
      <p className="text-xs text-[#A39E93] mt-2">Mostra as definições deste ecrã mesmo antes de guardar. O site só muda depois de Guardar e Reaplicar.</p>
    </div>
  );
}

function RewatermarkAll() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/admin/media/watermark-status").then((r) => setSt(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const running = st?.job?.status === "running";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!running) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [running]);
  if (!st) return null;
  const run = async () => {
    if (!window.confirm(`Reaplicar a marca de água atual a ${st.ready} foto(s) do catálogo? Guarde antes as definições se alterou opacidade ou tamanho.`)) return;
    try { await api.post("/admin/media/rewatermark-all"); toast.success("Atualização iniciada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const j = st.job;
  return (
    <div className="w-full mt-2 pt-4 border-t border-[#282520] text-sm" data-testid="rewatermark-all">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-gold !py-2 text-xs" disabled={running || !st.ready} onClick={run} data-testid="rewatermark-all-button">
          <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> {running ? `A atualizar… ${j.done}/${j.total || "?"} relógios` : "Reaplicar a todo o catálogo"}
        </button>
        <span className="text-xs text-[#A39E93]" data-testid="rewatermark-all-counts">{st.ready} foto(s) podem ser atualizadas · {st.missing} sem original</span>
      </div>
      {j && !running && <p className="text-xs text-[#A39E93] mt-2" data-testid="rewatermark-all-last">Última atualização ({j.status === "done" ? "concluída" : "falhou"}): {j.updated} foto(s) atualizadas{j.missing ? `, ${j.missing} sem original` : ""}.</p>}
      {!!st.watches_missing.length && (
        <details className="mt-3 text-xs" data-testid="rewatermark-missing-list">
          <summary className="cursor-pointer text-[#e0937f]">Relógios com fotos antigas sem original — volte a carregar estas fotos ({st.watches_missing.length})</summary>
          <ul className="mt-2 space-y-1">{st.watches_missing.map((w) => <li key={w.id}><Link to={`/admin/relogios/${w.id}`} className="underline hover:text-[#E6D5AC]">{w.name}</Link> <span className="text-[#A39E93]">· {w.missing} foto(s)</span></li>)}</ul>
        </details>
      )}
    </div>
  );
}
